import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Progress, 
  Button, 
  Space, 
  Collapse, 
  Tooltip,
  Typography
} from 'antd';
import { 
  MinusOutlined, 
  CreditCardOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { calculateAvailableCreditLimit, calculateCreditUtilization } from '../utils/creditUtils';

const { Text } = Typography;
const { Panel } = Collapse;

const UnifiedCreditDisplay = ({ 
  accountData, 
  loading = false, 
  showActions = true,
  compact = false 
}) => {
  const navigate = useNavigate();
  const [showBreakdown, setShowBreakdown] = useState(false);

  const {
    topUpBalance = 0,     // Prepaid credit
    creditLimit = 0,      // Credit limit
    creditUsed = 0,       // Credit used
    currency = 'USD',
  } = accountData || {};

  // Recalculate display data (unified method)
  const availableCreditLimit = calculateAvailableCreditLimit({ topUpBalance, creditLimit, creditUsed });
  const creditUtilization = calculateCreditUtilization({ topUpBalance, creditLimit, creditUsed });

  // Progress bar color logic
  const getProgressColor = (utilization) => {
    if (utilization >= 90) return '#ff4d4f';      // Red: 90%+
    if (utilization >= 75) return '#fa8c16';      // Orange: 75-90%
    if (utilization >= 50) return '#faad14';      // Yellow: 50-75%
    return '#52c41a';                             // Green: 0-50%
  };

  const formatCurrency = (amount) => {
    if (currency === 'HKD') return `HK$${amount.toFixed(0)}`;
    if (currency === 'MYR') return `MYR ${amount.toFixed(0)}`;
    if (currency === 'CNY') return `¥${amount.toFixed(0)}`;
    return `$${amount.toFixed(0)}`;
  };

  // Compact mode display
  if (compact) {
    return (
      <div className="credit-compact" style={{ 
        padding: '12px', 
        border: '1px solid #d9d9d9', 
        borderRadius: '6px',
        background: '#fafafa'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
              {formatCurrency(availableCreditLimit)}
            </div>
                         <div style={{ fontSize: '12px', color: '#666' }}>Available Credit Limit</div>
          </div>
          <div style={{ width: '60px' }}>
            <Progress 
              percent={creditUtilization}
              size="small"
              strokeColor={getProgressColor(creditUtilization)}
              format={() => null}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card 
      title={
        <Space>
          <CreditCardOutlined />
          <span>Credit Account Overview</span>
          <Tooltip title="Available Credit Limit = Prepaid Credit + Remaining Credit">
            <InfoCircleOutlined style={{ color: '#666' }} />
          </Tooltip>
        </Space>
      }
      loading={loading}
      extra={
        showActions && creditUsed > 0 && (
          <Button
            type="primary"
            icon={<MinusOutlined />}
            onClick={() => navigate('/client/account/repayment')}
          >
            Repay Credit
          </Button>
        )
      }
    >
      {/* Main display area */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <Statistic
          title={
            <span style={{ fontSize: '14px', color: '#666' }}>
              Available Credit Limit
            </span>
          }
          value={availableCreditLimit}
          valueStyle={{ 
            fontSize: '32px', 
            fontWeight: 'bold',
            color: availableCreditLimit > 0 ? '#52c41a' : '#ff4d4f' 
          }}
          prefix={currency === 'CNY' ? '¥' : '$'}
          suffix={currency !== 'CNY' ? currency : ''}
        />
        <div style={{ 
          fontSize: '12px', 
          color: '#999', 
          marginTop: '4px' 
        }}>
          Available Credit Limit {formatCurrency(availableCreditLimit)} | 
          Used {formatCurrency(creditUsed)}
        </div>
      </div>
      
      {/* Progress bar */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: '8px',
          fontSize: '12px',
          color: '#666'
        }}>
          <span>0</span>
          <span style={{ 
            fontWeight: 'bold', 
            color: getProgressColor(creditUtilization) 
          }}>
            {creditUtilization.toFixed(1)}% Utilization
          </span>
          <span>{formatCurrency(availableCreditLimit)}</span>
        </div>
        <Progress
          percent={creditUtilization}
          strokeColor={{
            '0%': '#52c41a',
            '50%': '#faad14', 
            '75%': '#fa8c16',
            '90%': '#ff4d4f'
          }}
          trailColor="#f0f0f0"
          strokeWidth={12}
          format={(percent) => (
            <span style={{ color: '#333', fontSize: '11px' }}>
              {formatCurrency(creditUsed)} / {formatCurrency(availableCreditLimit)}
            </span>
          )}
        />
      </div>

      {/* Quick info */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={8}>
          <div style={{ textAlign: 'center', padding: '12px', background: '#f6ffed', borderRadius: '6px' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
              {formatCurrency(topUpBalance)}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>Prepaid Credit</div>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ textAlign: 'center', padding: '12px', background: '#f0f9ff', borderRadius: '6px' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
              {formatCurrency(creditLimit)}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>Credit Line</div>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ textAlign: 'center', padding: '12px', background: creditUsed > 0 ? '#fff2e8' : '#f6ffed', borderRadius: '6px' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: creditUsed > 0 ? '#fa8c16' : '#52c41a' }}>
              {formatCurrency(creditUsed)}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>Used</div>
          </div>
        </Col>
      </Row>
      
      {/* Detail breakdown (collapsible) */}
      <Collapse 
        ghost 
        size="small"
        onChange={(key) => setShowBreakdown(key.length > 0)}
      >
        <Panel 
          header={
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {showBreakdown ? 'Hide' : 'Show'} Breakdown
            </Text>
          } 
          key="breakdown"
        >
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ 
                padding: '12px', 
                background: '#f6ffed', 
                borderRadius: '6px',
                border: '1px solid #d9f7be'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px'
                }}>
                                     <span style={{ fontSize: '13px', color: '#666' }}>Prepaid Credit</span>
                  <span style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#52c41a' 
                  }}>
                    +{formatCurrency(topUpBalance)}
                  </span>
                </div>
                                 <div style={{ fontSize: '11px', color: '#999' }}>
                   Prepaid funds, used first for purchases
                 </div>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ 
                padding: '12px', 
                background: '#f0f9ff', 
                borderRadius: '6px',
                border: '1px solid #b7f7ff'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px'
                }}>
                                     <span style={{ fontSize: '13px', color: '#666' }}>Credit Line</span>
                  <span style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#1890ff' 
                  }}>
                    +{formatCurrency(creditLimit)}
                  </span>
                </div>
                                 <div style={{ fontSize: '11px', color: '#999' }}>
                   Company approved credit limit, used when prepaid insufficient
                 </div>
              </div>
            </Col>
          </Row>
          
          {/* Deduction order note */}
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            background: '#fafafa', 
            borderRadius: '6px',
            border: '1px solid #e8e8e8'
          }}>
                         <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
               <strong>💡 Usage Order:</strong>
             </div>
             <div style={{ fontSize: '11px', color: '#999', lineHeight: '1.6' }}>
               1. Purchases: Prepaid credit first, then credit line automatically<br/>
               2. Repayments: Pay credit debt first, then excess becomes prepaid credit<br/>
               3. Top-ups: Pay off debt first if any, then add to prepaid credit
             </div>
          </div>
        </Panel>
      </Collapse>
      
      {/* Action buttons */}
      {showActions && creditUsed > 0 && (
        <div style={{ marginTop: '20px' }}>
          <Button
            type="primary"
            size="large"
            block
            icon={<MinusOutlined />}
            onClick={() => navigate('/client/account/repayment')}
          >
            Repay Credit ({formatCurrency(creditUsed)})
          </Button>
        </div>
      )}
    </Card>
  );
};

export default UnifiedCreditDisplay; 
